export const normalizeHex = (hexColor?: string): string | null => {
  if (!hexColor) return null;
  const hex = hexColor.trim();
  if (!/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }
  return hex.startsWith("#") ? hex : `#${hex}`;
};

export const adjustHexColor = (hex: string, amount: number): string | null => {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;

  const hexBody = normalized.replace("#", "");
  const num = parseInt(hexBody, 16);

  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const toRgb = (hex: string): [number, number, number] => {
  const num = parseInt(hex.replace("#", ""), 16);
  return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
};

const toHex = ([r, g, b]: [number, number, number]): string =>
  `#${((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b))
    .toString(16)
    .slice(1)}`;

export const mixHex = (hex: string, other: string, weight: number): string => {
  const a = normalizeHex(hex);
  const b = normalizeHex(other);
  if (!a || !b) return hex;
  const [r1, g1, b1] = toRgb(a);
  const [r2, g2, b2] = toRgb(b);
  const w = Math.max(0, Math.min(1, weight));
  return toHex([r1 + (r2 - r1) * w, g1 + (g2 - g1) * w, b1 + (b2 - b1) * w]);
};

/** Luminance relative WCAG. */
export const relativeLuminance = (hex: string): number => {
  const normalized = normalizeHex(hex);
  if (!normalized) return 0;
  const [r, g, b] = toRgb(normalized).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Rapport de contraste WCAG, de 1 (identiques) à 21 (noir sur blanc). */
export const contrastRatio = (hex: string, other: string): number => {
  const a = relativeLuminance(hex);
  const b = relativeLuminance(other);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

export const readableTextColor = (
  hex: string,
  background: string,
  minRatio = 4.5
): string => {
  let color = normalizeHex(hex);
  if (!color) return "#000000";
  for (let i = 0; i < 12 && contrastRatio(color, background) < minRatio; i++) {
    color = mixHex(color, "#000000", 0.15);
  }
  return color;
};

export const createGradientStyle = (
  accentColor: string,
  lightenAmount = 40,
  darkenAmount = -35
): { backgroundImage: string } => {
  const defaultColor = "#0ea5e9";
  const accentBase = normalizeHex(accentColor) || defaultColor;
  const lighter = adjustHexColor(accentBase, lightenAmount) || accentBase;
  const darker = adjustHexColor(accentBase, darkenAmount) || accentBase;

  return {
    backgroundImage: `linear-gradient(135deg, ${lighter}, ${darker})`,
  };
};
